import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useAdminStore } from '../store/adminStore'
import { adminSupabase } from '../lib/adminClient'

export function AdminProtectedRoute() {
  const { isAuthenticated, checkExpiry, refreshExpiry, logout } = useAdminStore()
  // Tracks the live Supabase session check so a desynced adminStore (which has
  // its own 2h TTL) can't keep rendering pages after the JWT has expired.
  const [sessionValid, setSessionValid] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false

    // Ties the UI's "logged in" state to the REAL Supabase session: if there's
    // no live session or it has expired, log out and let the redirect below
    // fire — eliminating the silent empty-page failure mode.
    async function validate() {
      const { data } = await adminSupabase.auth.getSession()
      const session = data.session
      const expired = session?.expires_at != null && session.expires_at * 1000 <= Date.now()
      const valid = !!session && !expired
      if (cancelled) return
      if (!valid) logout()
      setSessionValid(valid)
    }

    validate()

    // Periodic check so a session that expires mid-use redirects cleanly
    // rather than waiting for the next route change.
    const interval = window.setInterval(validate, 60 * 1000)

    // Refresh adminStore expiry on user activity
    const handler = () => refreshExpiry()
    window.addEventListener('mousemove', handler)
    window.addEventListener('keydown', handler)
    return () => {
      cancelled = true
      window.clearInterval(interval)
      window.removeEventListener('mousemove', handler)
      window.removeEventListener('keydown', handler)
    }
  }, [refreshExpiry, logout])

  // Wait for the first session check before deciding, so we don't flash the
  // login page for an authenticated admin with a valid token.
  if (sessionValid === null) return null

  if (!sessionValid || !isAuthenticated || !checkExpiry()) {
    return <Navigate to="/admin/login" replace />
  }

  return <Outlet />
}
